/** The activity's fact band (plan §3 "Band", §2 "Fact band"): five cells of facts, not big
 *  numbers — a title, a datum line and a muted detail line — each leading to what owns its
 *  facts. Pure: `now` is a parameter.
 *
 *  | Cell            | Datum                         | Detail                                     | Leads to        |
 *  |-----------------|-------------------------------|--------------------------------------------|-----------------|
 *  | Type & category | the type's word               | the category / "No category"               | the About card  |
 *  | Place           | the address / "No address"    | zone · "no coordinates"                    | the Place card  |
 *  | Used by         | "2 itineraries · 1 stop"      | "9 tours · next 14 Oct" (the summary)      | the Usage tab   |
 *  |                 |  (the detail's cheap counts)  |  + how far an edit reaches the app (D8)    |                 |
 *  | Content         | "104 images"                  | "2 documents · 4.6★ · 2,054 reviews from    | the Images tab  |
 *  |                 |                               |  3 sources" (the total over its sources)   |                 |
 *  | Source          | `catalogSourceLabel`          | "Google place linked" / "no place"          | Identity card   |
 *
 *  The used-by summary (D7) is read lazily, after the detail:
 *  `GET activities/<pk>/used_by/summary/` (`usedBySummary`). Until it arrives the Used by detail
 *  is the app-reach line; above 500 tours it reads "500+ tours" (no departures counted); when
 *  the database cancelled its read, "Tours not counted (timed out)" before the app-reach line.
 *  Dates are in the activity's own zone (UTC when it has none).
 *
 *  A cell leads to what owns its facts, never off the record: the Place cell opens the
 *  Place card, whose address carries the map link (`activityMapHref`) in its value
 *  actions. */
import type {
  ActivityDetail,
  ActivityRatings,
  ActivityUsage,
  ActivityUsedBySummary,
} from '@/api/activities';
import { appReachLine, catalogSourceLabel, notCounted } from '@/features/catalog/words';
import { formatZoned } from '@/utils/zonedTime';

import { activityTypeLabel } from './activityNames';

/** The activity record's page tabs. */
export type ActivityRecordTab = 'activity' | 'images' | 'usage' | 'history';

/** The record's cards a band cell can lead to. */
export type ActivityCardKey = 'about' | 'place' | 'identity';

export type ActivityBandTarget =
  | { kind: 'tab'; tab: ActivityRecordTab }
  | { kind: 'card'; card: ActivityCardKey };

export type ActivityBandCellId = 'type' | 'place' | 'used' | 'content' | 'source';

export interface ActivityBandCell {
  id: ActivityBandCellId;
  title: string;
  datum: string;
  detail: string | null;
  target: ActivityBandTarget;
}

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

const hasCoordinates = (a: Pick<ActivityDetail, 'latitude' | 'longitude'>) =>
  typeof a.latitude === 'number' &&
  typeof a.longitude === 'number' &&
  Number.isFinite(a.latitude) &&
  Number.isFinite(a.longitude);

/** "14 Oct" (the year when it is not this year), in `zone`. */
function bandDay(iso: string, zone: string | null | undefined, now: Date): string {
  return formatZoned(iso, zone || 'UTC', { weekday: false, time: false, zone: false, now });
}

function typeCell(a: ActivityDetail): ActivityBandCell {
  return {
    id: 'type',
    title: 'Type & category',
    datum: activityTypeLabel(a),
    detail: a.category ? a.category.title || `Category #${a.category.id}` : 'No category',
    target: { kind: 'card', card: 'about' },
  };
}

/** Where the activity sits on the map: its Google place, else its coordinates; null with
 *  neither. The Place card's map link (its address value actions, `act.external`) — not a
 *  band target: the band's Place cell opens the card that owns the place. */
export function activityMapHref(
  a: Pick<ActivityDetail, 'place' | 'latitude' | 'longitude'>,
): string | null {
  if (a.place?.link) return a.place.link;
  if (!hasCoordinates(a)) return null;
  return `https://www.google.com/maps/search/?api=1&query=${a.latitude},${a.longitude}`;
}

function placeCell(a: ActivityDetail): ActivityBandCell {
  const detail = [a.timezone?.trim() || null, hasCoordinates(a) ? null : 'no coordinates'].filter(
    Boolean,
  );
  return {
    id: 'place',
    title: 'Place',
    datum: a.address?.trim() || a.country_name?.trim() || 'No address',
    detail: detail.length ? detail.join(' · ') : null,
    target: { kind: 'card', card: 'place' },
  };
}

/** "2 itineraries · 1 stop": the catalog itineraries and line-set stops among the cheap
 *  counts; empty when there are none. */
function itineraryAndStopCounts(usage: Pick<ActivityUsage, 'catalog_itineraries' | 'stops'>): string[] {
  return [
    usage.catalog_itineraries
      ? plural(usage.catalog_itineraries, 'itinerary', 'itineraries')
      : null,
    usage.stops ? plural(usage.stops, 'stop') : null,
  ].filter((p): p is string => !!p);
}

/** The cheap counts: "2 itineraries · 1 stop · nearby of 3 activities". */
export function usedByCounts(usage: ActivityUsage): string {
  const parts = [
    ...itineraryAndStopCounts(usage),
    usage.nearby_of ? `nearby of ${plural(usage.nearby_of, 'activity', 'activities')}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'No itinerary or stop';
}

/** The Used by detail: the summary's tours and next departure, then how far an edit reaches
 *  the app — told the summary's upcoming tours, which the detail never carries. Without the
 *  summary, the app-reach line alone; a cancelled read says so before it. */
function usedByDetail(
  a: Pick<ActivityDetail, 'app_reach' | 'timezone'>,
  summary: ActivityUsedBySummary | null | undefined,
  now: Date,
): string {
  if (!summary) return appReachLine('activity', a.app_reach);
  const { tours } = summary;
  if (!summary.available || tours === null) {
    return `${notCounted('Tours', summary.reason)} · ${appReachLine('activity', a.app_reach)}`;
  }
  // No tours: nothing waits on a tour cache.
  if (tours === 0) return 'No tours';
  const reach = appReachLine('activity', {
    ...a.app_reach,
    tours_upcoming: summary.tours_upcoming,
  });
  // Capped: `tours` stops at 500 and no departure is counted.
  const counted = summary.capped
    ? `${tours.toLocaleString('en-US')}+ tours`
    : plural(tours, 'tour');
  const next = summary.next_departure
    ? ` · next ${bandDay(summary.next_departure, a.timezone, now)}`
    : '';
  return `${counted}${next} · ${reach}`;
}

function usedCell(
  a: ActivityDetail,
  summary: ActivityUsedBySummary | null | undefined,
  now: Date,
): ActivityBandCell {
  return {
    id: 'used',
    title: 'Used by',
    datum: usedByCounts(a.usage),
    detail: usedByDetail(a, summary, now),
    target: { kind: 'tab', tab: 'usage' },
  };
}

/** "4.6★ · 2,054 reviews from 3 sources" — the rating stored on the activity: the total over
 *  its rating sources (`sources`, the detail's `rating_count`), NOT what the app shows (the apps
 *  show one source's rating). "1 review"; "from N sources" only with more than one source; the
 *  stars alone without a review count; null without a rating. */
export function ratingText(ratings: ActivityRatings, sources: number): string | null {
  if (typeof ratings.star_rating !== 'number') return null;
  const stars = `${ratings.star_rating.toFixed(1)}★`;
  if (ratings.review_count <= 0) return stars;
  const from = sources > 1 ? ` from ${plural(sources, 'source')}` : '';
  return `${stars} · ${plural(ratings.review_count, 'review')}${from}`;
}

function contentCell(a: ActivityDetail): ActivityBandCell {
  const { images, documents, ratings } = a.usage;
  return {
    id: 'content',
    title: 'Content',
    datum: images ? plural(images, 'image') : 'No images',
    detail: [
      documents ? plural(documents, 'document') : 'no documents',
      ratingText(ratings, a.rating_count),
    ]
      .filter(Boolean)
      .join(' · '),
    target: { kind: 'tab', tab: 'images' },
  };
}

function sourceCell(a: ActivityDetail, now: Date): ActivityBandCell {
  return {
    id: 'source',
    title: 'Source',
    datum: catalogSourceLabel(a.source, now),
    detail: a.place ? 'Google place linked' : 'no place',
    target: { kind: 'card', card: 'identity' },
  };
}

/** The band's cells, in order. `summary` is the used-by summary once it is read (D7). */
export function activityFactBandCells(
  a: ActivityDetail,
  { summary = null, now = new Date() }: { summary?: ActivityUsedBySummary | null; now?: Date } = {},
): ActivityBandCell[] {
  return [typeCell(a), placeCell(a), usedCell(a, summary, now), contentCell(a), sourceCell(a, now)];
}
